// src/locales/English/en.advertisementGuide.ts

const advertisementGuide = {
  advertisementGuide: {
    meta: {
      title: "Advertisement Guide",
      subtitle: "Create campaigns, target the right audience, manage budgets, and stay compliant.",
      back: "Back",
      overviewTitle: "Advertisement Guide",
      searchPlaceholder: "Search Advertisement Guide",
    },

    sidebar: {
      adGuide: "Advertisement Guide", 
      campaignCreation: "Campaign Creation",
      targetingDelivery: "Targeting & Delivery",
      reviewModeration: "Review & Moderation",
      performanceBilling: "Performance & Billing",
      bestPractices: "Best Practices",
      compliance: "Safety & Compliance",
      troubleshooting: "Troubleshooting",
    },

    overview: {
      advertisingOverview: "Advertising overview",
      whoCanAdvertise: "Who can advertise?",
      beforeYouStart: "Before you start",

      createCampaign: "Create a campaign",
      campaignUrl: "Campaign URL",
      buttonText: "Button text",
      uploadAdMedia: "Upload ad media",
      campaignDetails: "Campaign details",
      launchCampaign: "Launch campaign",  

      targetLocation: "Target location",
      deliveryModes: "Delivery modes",
      budgetSetup: "Budget setup",
      campaignFrequency: "Campaign frequency",

      adReview: "Ad review",
      pendingApproval: "Pending approval",
      rejectionReasons: "Rejection reasons",
      editRejectedAd: "Edit a rejected ad",

      pricingCpm: "Pricing and CPM",
      spendCounting: "When spend is counted",
      campaignStats: "Campaign statistics",
      billingInvoices: "Billing and invoices",

      creativeBestPractices: "Creative best practices",
      landingPageQuality: "Landing page quality",
      avoidAdFatigue: "Avoid ad fatigue",

      prohibitedContent: "Prohibited content",
      responsibleAdvertising: "Responsible advertising",
      copyrightAndMediaRights: "Copyright and media rights",

      invalidUrl: "Invalid URL",
      unsupportedMedia: "Unsupported media",
      campaignNotDelivering: "Campaign not delivering",
      contactAdsSupport: "Contact ads support",
    },

    sections: {
      advertisingOverview: {
        title: "Advertising overview",
        body: [
          {
            type: "paragraph",
            text: "Advertising lets you create paid campaigns that appear as promoted content on the platform.",
          },
          {
            type: "bullets",
            items: [
              "Create ad campaigns with media, URL, button text, targeting, and budget.",
              "Campaigns are reviewed before they begin delivering.",
              "Performance depends on creative quality, targeting, budget, and delivery settings.",
            ],
          },
        ],
      },

      createCampaign: {
        title: "Create a campaign",
        body: [
          {
            type: "paragraph",
            text: "To create an advertisement, start a new campaign and complete the required campaign setup.",
          },
          {
            type: "steps",
            items: [
              "Open Advertising.",
              "Click '+ Create new campaign'.",
              "Add a valid campaign URL.",
              "Choose the button text for your ad.",
              "Upload your advertising media.",
              "Click Continue.",
              "Add campaign details such as name, target location, budget, and delivery frequency.",
              "Click Launch Campaign & Upload.",
            ],
          },
          {
            type: "note",
            text: "After launch, your ad will be reviewed before it starts delivering.",
          },
        ],
      },

      campaignUrl: {
        title: "Campaign URL",
        body: [
          {
            type: "paragraph",
            text: "The campaign URL is the destination users visit when they click the button on your advertisement.",
          },
          {
            type: "bullets",
            items: [
              "Use a valid URL that starts with https://.",
              "Make sure the page loads correctly.",
              "The landing page should match the promise of your ad.",
              "Avoid broken links, misleading redirects, or unrelated pages.",
            ],
          },
        ],
      },

      uploadAdMedia: {
        title: "Upload ad media",
        body: [
          {
            type: "paragraph",
            text: "Your ad media is the visual content users see in the feed.",
          },
          {
            type: "bullets",
            items: [
              "Use clear, high-quality images or videos.",
              "Avoid small text that is hard to read.",
              "Use media you own or have permission to use.",
              "Make sure your media follows advertising and platform rules.",
            ],
          },
        ],
      },

      targetLocation: {
        title: "Target location",
        body: [
          {
            type: "paragraph",
            text: "Location targeting helps you decide where your advertisement should be shown.",
          },
          {
            type: "bullets",
            items: [
              "Choose worldwide targeting for broad campaigns.",
              "Choose country, continent, or city targeting for more specific campaigns.",
              "Use city targeting for local businesses or events.",
              "Avoid targeting too narrowly if you want more delivery volume.",
            ],
          },
        ],
      },

      deliveryModes: {
        title: "Delivery modes",
        body: [
          {
            type: "paragraph",
            text: "Delivery modes control how quickly your campaign budget is used.",
          },
          {
            type: "bullets",
            items: [
              "Light delivery spends more slowly.",
              "Balanced delivery aims for stable campaign pacing.",
              "Frequent delivery may spend faster and reach users more aggressively.",
              "Use Frequent for limited-time offers or short campaign windows.",
            ],
          },
        ],
      },

      budgetSetup: {
        title: "Budget setup",
        body: [
          {
            type: "paragraph",
            text: "Your budget controls how much your campaign can spend.",
          },
          {
            type: "bullets",
            items: [
              "Start with a modest budget when testing a new campaign.",
              "Increase budgets gradually after reviewing performance.",
              "Campaign delivery may fluctuate depending on audience and demand.",
            ],
          },
        ],
      },

      adReview: {
        title: "Ad review",
        body: [
          {
            type: "paragraph",
            text: "Ads are reviewed before they can begin delivering.",
          },
          {
            type: "bullets",
            items: [
              "Review may include automated or manual checks.",
              "Editing an ad during review may restart the review process.",
              "Approved ads can deliver once campaign settings are active.",
              "Rejected ads need to be corrected before they can run.",
            ],
          },
        ],
      },

      prohibitedContent: {
        title: "Prohibited content",
        body: [
          {
            type: "paragraph",
            text: "Advertisements must not contain content that violates platform rules, applicable law, or safety standards.",
          },
          {
            type: "bullets",
            items: [
              "Do not use misleading claims or fake offers.",
              "Do not promote harassment, hate, violence, scams, or illegal activity.",
              "Do not use copyrighted media unless you have the rights.",
              "Make sure regulated products follow applicable laws and restrictions.",
            ],
          },
        ],
      },

      contactAdsSupport: {
        title: "Contact ads support",
        body: [
          {
            type: "paragraph",
            text: "Contact support if you cannot solve an advertising issue yourself.",
          },
          {
            type: "bullets",
            items: [
              "Include the campaign name or campaign ID if available.",
              "Describe the issue clearly.",
              "Mention when the problem started.",
              "Attach screenshots if helpful.",
            ],
          },
        ],
      },
    },
  },
} as const;

export default advertisementGuide; 